import type { StepNode } from '@segmently/schema-models/v2';
import type { ConnectionHandle } from '@modules/reactFlow/types/flow-canvas-types';
import { getV2ScreenTypeConfig } from '../../config/v2ScreenTypeConfig';
import { HANDLE_DIMENSIONS, HANDLE_REGIONS } from '../../constants/handleConfig';
import { CANVAS_NODE_DIMENSIONS } from '../../constants/screenPreviewConfig';
import { TransitionIdGenerator } from '../../utils/converters/TransitionIdGenerator';
import {
  getV2ActionableSectionItems,
  V2_ACTIONABLE_SECTION_ITEM_KINDS,
} from './V2SectionActionDiscovery';

type StepWithCanvasId = StepNode & {
  entityId?: string;
  screenType?: string;
};

interface PendingHandle {
  actionKey: string;
  label: string;
  y: number;
}

const ITEM_SPACING = 93;
const SECTION_SLOT = 60;
const SECTION_ITEM_SPACING = 25;
const STICKY_BASE_Y = HANDLE_REGIONS.MIDDLE_END + 30;
const STICKY_SLOT = 40;
const STICKY_ITEM_SPACING = 25;
const MIN_HANDLE_Y = 0;
const MAX_HANDLE_Y = CANVAS_NODE_DIMENSIONS.HEIGHT - HANDLE_DIMENSIONS.HEIGHT;
const MIN_HANDLE_GAP = HANDLE_DIMENSIONS.HEIGHT + 4;

const ACTIONBAR_DISABLED_KINDS = new Set([
  'Carousel',
  'ProgressSteps',
]);

const FLEXIBLE_ITEM_ACTION_KINDS = V2_ACTIONABLE_SECTION_ITEM_KINDS;

function extractV2Text(label: any, fallback: string): string {
  if (!label) return fallback;
  const t = label.translations;
  if (!t || typeof t !== 'object') return fallback;
  return t['en-US'] || t.en || Object.values(t)[0] as string || fallback;
}

function clampY(y: number): number {
  return Math.min(MAX_HANDLE_Y, Math.max(MIN_HANDLE_Y, y));
}

function normalizeFlexibleHandlePositions(handles: PendingHandle[]): PendingHandle[] {
  if (handles.length <= 1) {
    return handles.map((handle) => ({ ...handle, y: clampY(handle.y) }));
  }

  const ordered = handles
    .map((handle, index) => ({ ...handle, index }))
    .sort((a, b) => a.y - b.y || a.index - b.index);

  const normalized = ordered.map((handle) => ({ ...handle, y: clampY(handle.y) }));

  for (let i = 1; i < normalized.length; i += 1) {
    normalized[i].y = Math.max(normalized[i].y, normalized[i - 1].y + MIN_HANDLE_GAP);
  }

  const overflow = normalized[normalized.length - 1].y - MAX_HANDLE_Y;
  if (overflow > 0) {
    for (const handle of normalized) {
      handle.y -= overflow;
    }
  }

  if (normalized[0].y < MIN_HANDLE_Y) {
    const span = MAX_HANDLE_Y - MIN_HANDLE_Y;
    const step = span / Math.max(1, normalized.length - 1);
    normalized.forEach((handle, index) => {
      handle.y = MIN_HANDLE_Y + (step * index);
    });
  }

  return normalized.map(({ index, ...handle }) => handle);
}

function getSortedSections(sections: any[] | undefined): any[] {
  return [...(sections ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

function getSectionAnchorY(sortedSections: any[], kind: string): number | null {
  const index = sortedSections.findIndex((section) => section?.kind === kind);
  if (index === -1) return null;
  return HANDLE_REGIONS.MIDDLE_START + (index * SECTION_SLOT);
}

function toConnectionHandles(step: StepWithCanvasId, pendingHandles: PendingHandle[]): ConnectionHandle[] {
  const stepId = step.entityId || step.id;

  return pendingHandles.map((handle) => ({
    id: TransitionIdGenerator.generateHandleId(stepId, handle.actionKey),
    sourceActionKey: handle.actionKey,
    position: { x: 0, y: handle.y },
    label: handle.label,
    elementType: 'button',
    elementText: handle.label,
  }));
}

export function buildV2ActionHandles(step: StepWithCanvasId): ConnectionHandle[] {
  const pendingHandles: PendingHandle[] = [];
  const c = step.content;
  if (!c) return [];

  const kind = step.screenType || '';
  const isFlexibleLayout = kind === 'FlexibleLayout';
  const sortedFlexibleSections = getSortedSections(c.flexibleLayout?.sections as any[] | undefined);
  const flexibleHeaderY = isFlexibleLayout ? getSectionAnchorY(sortedFlexibleSections, 'Header') : null;
  const flexibleFooterY = isFlexibleLayout ? getSectionAnchorY(sortedFlexibleSections, 'Footer') : null;

  const addHandle = (actionKey: string, label: string, y: number): void => {
    pendingHandles.push({ actionKey, label, y });
  };

  const actionBarY = isFlexibleLayout
    ? flexibleFooterY
    : CANVAS_NODE_DIMENSIONS.HEIGHT - HANDLE_REGIONS.BOTTOM_OFFSET;

  const canRenderActionBar = actionBarY !== null && !ACTIONBAR_DISABLED_KINDS.has(kind);
  if (canRenderActionBar) {
    if (c.actionBar?.primary) {
      addHandle('actionBar.primary', extractV2Text(c.actionBar.primary?.content, 'Primary Button'), actionBarY);
    }
    if (c.actionBar?.secondary) {
      addHandle('actionBar.secondary', extractV2Text(c.actionBar.secondary?.content, 'Secondary Button'), actionBarY + 30);
    }
    if ((c.actionBar as any)?.purchase) {
      addHandle('actionBar.purchase', extractV2Text((c.actionBar as any).purchase?.content, 'Purchase'), actionBarY - 60);
    }
  }

  const headerY = isFlexibleLayout ? flexibleHeaderY : HANDLE_REGIONS.TOP_START;
  if (headerY !== null) {
    if ((c.header as any)?.close) {
      addHandle('header.close', extractV2Text((c.header as any).close?.content, 'Close'), headerY);
    }
    if (c.header?.skip) {
      addHandle('header.skip', extractV2Text(c.header.skip?.content, 'Skip'), headerY + 30);
    }
  }

  if ((c as any).paywall?.closeButton?.action) {
    addHandle('paywall.closeButton', 'Close', HANDLE_REGIONS.TOP_START);
  }
  if ((c as any).paywall?.purchaseAction?.action) {
    addHandle(
      'paywall.purchaseAction',
      extractV2Text((c as any).paywall.purchaseAction?.content, 'Purchase'),
      CANVAS_NODE_DIMENSIONS.HEIGHT - HANDLE_REGIONS.BOTTOM_OFFSET,
    );
  }
  if ((c as any).paywall?.purchaseAction?.action?.onCancelAction) {
    addHandle('paywall.purchaseCancel', 'Downsale', CANVAS_NODE_DIMENSIONS.HEIGHT - HANDLE_REGIONS.BOTTOM_OFFSET + 30);
  }

  const screenConfig = getV2ScreenTypeConfig(kind);
  if (screenConfig.itemsHaveActions !== false && c.options?.items) {
    c.options.items.forEach((item, i) => {
      addHandle(`options.item.${i}`, extractV2Text((item as any).title, `Option ${i + 1}`), HANDLE_REGIONS.MIDDLE_START + (i * ITEM_SPACING));
    });
  }

  if (c.carousel?.timer) {
    addHandle('carousel.timer', 'Carousel Timer', (HANDLE_REGIONS.MIDDLE_START + HANDLE_REGIONS.MIDDLE_END) / 2);
  }
  if (c.stepper?.timer) {
    addHandle('stepper.timer', 'Stepper Timer', (HANDLE_REGIONS.MIDDLE_START + HANDLE_REGIONS.MIDDLE_END) / 2);
  }
  if (c.countdown) {
    addHandle('countdown', 'Countdown', HANDLE_REGIONS.MIDDLE_START + 50);
  }
  if (c.systemPrompt) {
    addHandle('systemPrompt', 'System Prompt', HANDLE_REGIONS.MIDDLE_START + 100);
  }
  if (c.embed?.callback) {
    addHandle('embed.callback', 'Callback', HANDLE_REGIONS.MIDDLE_START);
  }

  const addSectionItemHandles = (
    section: any,
    sectionId: string,
    baseY: number,
    sectionLabel: string,
  ): void => {
    getV2ActionableSectionItems(section).forEach((item: any, itemIndex: number) => {
      addHandle(
        `section.${sectionId}.item.${itemIndex}`,
        extractV2Text(item.title, `${sectionLabel} Option ${itemIndex + 1}`),
        baseY + (itemIndex * SECTION_ITEM_SPACING),
      );
    });
  };

  const addSectionButtonHandle = (
    section: any,
    sectionId: string,
    y: number,
    fallbackLabel: string,
  ): void => {
    if (section.kind !== 'Button' || !section.buttonContent?.action) return;
    addHandle(
      `section.${sectionId}.button`,
      extractV2Text(section.buttonContent?.content, fallbackLabel),
      y,
    );
  };

  ((c.embed as any)?.childSections as any[] | undefined)?.forEach((section: any, index: number) => {
    if (!section.id) return;
    addSectionButtonHandle(
      section,
      section.id as string,
      HANDLE_REGIONS.MIDDLE_START + SECTION_ITEM_SPACING + (index * SECTION_SLOT),
      extractV2Text(section.label, `Data Source ${index + 1} Button`),
    );
    addSectionItemHandles(
      section,
      section.id as string,
      HANDLE_REGIONS.MIDDLE_START + SECTION_ITEM_SPACING + (index * SECTION_SLOT),
      extractV2Text(section.label, `Data Source ${index + 1}`),
    );
  });

  sortedFlexibleSections.forEach((section: any, index: number) => {
    if (!section.id) return;
    const sectionId = section.id as string;
    const sectionBaseY = HANDLE_REGIONS.MIDDLE_START + (index * SECTION_SLOT);

    if (section.buttonContent?.action) {
      addHandle(
        `section.${sectionId}.button`,
        extractV2Text(section.buttonContent?.content, `Button ${index + 1}`),
        sectionBaseY,
      );
    }
    if (section.buttonContent?.action?.onCancelAction) {
      addHandle(`section.${sectionId}.purchaseCancel`, 'Downsale', sectionBaseY + SECTION_ITEM_SPACING);
    }
    if (section.embedContent?.callback?.action) {
      addHandle(`section.${sectionId}.callback`, `Callback ${index + 1}`, sectionBaseY);
    }
    if (FLEXIBLE_ITEM_ACTION_KINDS.has(section.kind)) {
      addSectionItemHandles(section, sectionId, sectionBaseY, `Section ${index + 1}`);
    }
    ((section.embedContent as any)?.childSections as any[] | undefined)?.forEach((childSection: any, childIndex: number) => {
      if (!childSection.id) return;
      addSectionButtonHandle(
        childSection,
        childSection.id as string,
        sectionBaseY + SECTION_ITEM_SPACING + (childIndex * SECTION_SLOT),
        extractV2Text(childSection.label, `Section ${index + 1} Data Source ${childIndex + 1} Button`),
      );
      addSectionItemHandles(
        childSection,
        childSection.id as string,
        sectionBaseY + SECTION_ITEM_SPACING + (childIndex * SECTION_SLOT),
        extractV2Text(childSection.label, `Section ${index + 1} Data Source ${childIndex + 1}`),
      );
    });
    if (section.paywallHeaderContent?.closeButton?.action) {
      addHandle(`section.${sectionId}.close`, 'Close', sectionBaseY);
    }
  });

  const stickySections = getSortedSections(c.flexibleLayout?.stickyContainer?.sections as any[] | undefined);
  stickySections.forEach((section: any, index: number) => {
    if (!section.id) return;
    const sectionId = section.id as string;
    const stickyBaseY = STICKY_BASE_Y + (index * STICKY_SLOT);

    if (section.buttonContent?.action) {
      addHandle(
        `sticky.${sectionId}.button`,
        extractV2Text(section.buttonContent?.content, `Sticky Button ${index + 1}`),
        stickyBaseY,
      );
    }
    if (section.buttonContent?.action?.onCancelAction) {
      addHandle(`sticky.${sectionId}.purchaseCancel`, 'Sticky Downsale', stickyBaseY + STICKY_ITEM_SPACING);
    }
  });

  const boundedHandles = isFlexibleLayout || kind === 'WebEmbed'
    ? normalizeFlexibleHandlePositions(pendingHandles)
    : pendingHandles;

  return toConnectionHandles(step, boundedHandles);
}

export function getV2ValidActionKeys(step: StepWithCanvasId): Set<string> {
  return new Set(buildV2ActionHandles(step).map((handle) => handle.sourceActionKey));
}
